import {defineStore} from "pinia";
import {AvailabilityBoat} from "../models/Boat";
import * as CountryService from "../services/country.service";
import * as RegionService from "../services/region.service";
import * as LocationService from "../services/location.service";
import * as CityService from "../services/city.service";
import * as SearchableService from "../services/searchable.service";
import * as AvailabilityService from "../services/availability.service";
import * as CoreService from "../api";
import * as DateUtils from "@/helpers/dateUtils";
import * as DestinationUtils from "@/helpers/destinationUtils";
import {computed, ref, watch} from "vue";
import { useRouter, useRoute } from "vue-router";


import QueryBuilder from "@/models/Query";


export const useSearchStore = defineStore('search', () => {

    const state = ref('idle');
    const isPageLoading = ref(true);
    const isInitialSetup = ref(true);
    const locationChanged = ref(false);
    const availability = ref<AvailabilityBoat[]>([]);
    const filterMetas = ref({});
    const paginationData = ref({});
    const resultCount = ref(0);
    const router = useRouter();
    const route = useRoute();

    const filters = ref({
        locations: [],
        categories: [],
        destinations: [],
        destinationsEnabled: false,
        shipyards: [],
        models: [],
        charterTypes: [],
        sailTypes: [],
    });

    const defaultSelection = {
        dateFrom: new Date(),
        dateTo: new Date(),
        dateMonths: [],
        dateTab: 'Dates',
        sort: 'desc',
        price: [0, QueryBuilder.MAX],
        categories: [],
        destinations: [],
        shipyards: [],
        models: [],
        charterTypes: [],
        sailTypeIds: [],
        passengers: 0,
        cabins: [0, QueryBuilder.MAX],
        berths: [0, QueryBuilder.MAX],
        wc: [0, QueryBuilder.MAX],
        length: [0, QueryBuilder.MAX],
        year: [0, QueryBuilder.MAX],
        shouldHaveDirectBooking: false,
        shouldHaveSkipper: false,
    }
    const categories = ref([])

    const selectedFilters = ref({...defaultSelection});

    const updatePageUrl = () => {
        const queryParams = { ...query.value };
        
        if (queryParams.yachtCategoryIds) {
            queryParams.categories = Array.isArray(queryParams.yachtCategoryIds)
                ? queryParams.yachtCategoryIds.join(',')
                : queryParams.yachtCategoryIds;
            delete queryParams.yachtCategoryIds;
        }
        
        if (selectedFilters.value.passengers && selectedFilters.value.passengers > 0) {
            queryParams.passengers = selectedFilters.value.passengers;
        }
 
        queryParams.skipper = selectedFilters.value.shouldHaveSkipper ? "true" : "false";
 
        router.replace({
            path: route.path,
            query: queryParams,
        });
    };
    
    function onClearFilters() {
        selectedFilters.value = {
            ...defaultSelection
        }
    }

    const computeSelectedFiltersCountries = computed(() => {
        return selectedFilters.value.destinations.filter((c) => c.indexOf("country") == 0).map((c) => c.replace("country:", ""))
    });

    const computeSelectedFiltersRegions = computed(() => {
        return selectedFilters.value.destinations.filter((c) => c.indexOf("region") == 0).map((c) => c.replace("region:", ""))
    });

    const computeSelectedFiltersLocations = computed(() => {
        return selectedFilters.value.destinations.filter((c) => c.indexOf("harbour") == 0).map((c) => c.replace("harbour:", ""))
    });
    const computeSelectedFiltersCities = computed(() => {
        return selectedFilters.value.destinations.filter((c) => c.indexOf("location") == 0).map((c) => c.replace("location:", ""))
    });


    const computedOrderedSailItems = computed(() => {
        const order = [16, 15, 14];
        const sorted = filters.value.sailTypes.sort((a, b) => {
            return order.indexOf(a.id) - order.indexOf(b.id);
        });
        //console.log("Sail items:", sorted);
        return sorted;
    });

    const isLoading = computed(() => {
        console.log("LOADING")
        return state.value === 'loading'
    })

    const isLoadingMore = computed(() => {
        return state.value === 'loadingMore'
    })
    const isError = computed(() => {
        return state.value === 'error'
    })
    const isNoResults = computed(() => {
        return state.value === 'noResults'
    })

    const query = computed(() => {


        return new QueryBuilder()
            .dateRange(
                DateUtils.format(selectedFilters.value.dateFrom),
                DateUtils.format(selectedFilters.value.dateTo)
            )
            .price(selectedFilters.value.price[0], selectedFilters.value.price[1])
            .cabins(selectedFilters.value.cabins[0], selectedFilters.value.cabins[1])
            .years(selectedFilters.value.year[0], selectedFilters.value.year[1])
            .berths(selectedFilters.value.berths[0], selectedFilters.value.berths[1])
            .wc(selectedFilters.value.wc[0], selectedFilters.value.wc[1])
            .length(selectedFilters.value.length[0], selectedFilters.value.length[1])
            .charterTypeIds(selectedFilters.value.charterTypes)
            .modelIds(selectedFilters.value.models)
            .shipyardIds(selectedFilters.value.shipyards)
            .categoryIds(selectedFilters.value.categories)
            .sailTypeIds(selectedFilters.value.sailTypeIds)
            .regionIds(computeSelectedFiltersRegions.value)
            .countryIds(computeSelectedFiltersCountries.value)
            .locationIds(computeSelectedFiltersLocations.value)
            .cityIds(computeSelectedFiltersCities.value)
            .sortBy("finalPrice", selectedFilters.value.sort)
            .build()

    })

    const recommendationQuery = computed(() => {
        const qb = new QueryBuilder()
            .dateRange(
                DateUtils.format(selectedFilters.value.dateFrom),
                DateUtils.format(selectedFilters.value.dateTo)
            )
            .price(selectedFilters.value.price[0], selectedFilters.value.price[1])
            .cabins(selectedFilters.value.cabins[0], selectedFilters.value.cabins[1])
            .years(selectedFilters.value.year[0], selectedFilters.value.year[1])
            .berths(selectedFilters.value.berths[0], selectedFilters.value.berths[1])
            .wc(selectedFilters.value.wc[0], selectedFilters.value.wc[1])
            .length(selectedFilters.value.length[0], selectedFilters.value.length[1])
            .charterTypeIds(selectedFilters.value.charterTypes)
            .modelIds(selectedFilters.value.models)
            .shipyardIds(selectedFilters.value.shipyards)
            .passengers(selectedFilters.value.passengers)
            .skipper(selectedFilters.value.shouldHaveSkipper)
            .sortBy("finalPrice", "asc")

            if(selectedFilters.value.destinations.length > 1){
                const destination = selectedFilters.value.destinations[0]
                if(destination.indexOf("harbour") ==0 ){
                    qb.locationIds(destination.replace("harbour:", ""))
                }
                if(destination.indexOf("region") ==0 ){
                    qb.regionIds(destination.replace("region:", ""))
                }
                if(destination.indexOf("country") ==0 ){
                    qb.countryIds(destination.replace("country:", ""))
                }
                if(destination.indexOf("location") ==0 ){
                    qb.cityIds(destination.replace("location:", ""))
                }

            }else{
                qb.regionIds(computeSelectedFiltersRegions.value)
                .countryIds(computeSelectedFiltersCountries.value)
                .locationIds(computeSelectedFiltersLocations.value)
                .cityIds(computeSelectedFiltersCities.value)
            }

            if(selectedFilters.value.categories.length > 1){
                qb.categoryIds(selectedFilters.value.categories[0])
            }else{
                qb.categoryIds(selectedFilters.value.categories)
            }

            return qb.build(true, {
                'minCabins': 'cabins',
                'yachtCategoryIds': 'categories'
            })

    })

    const toggleSort = () => {
        selectedFilters.value.sort = selectedFilters.value.sort === 'asc' ? 'desc' : 'asc';
    };

    const setDates = (dates) => {

        if (dates[0] == null || dates[1] == null) {
            return
        }

        selectedFilters.value.dateFrom = dates[0];
        selectedFilters.value.dateTo = dates[1];
        isInitialSetup.value = true
    }


    async function search() {
        state.value = 'loading';
        try {

            let availabilityResponse
            availabilityResponse = await AvailabilityService.searchAvailabilities(query.value);

            if (isInitialSetup.value) {
                filterMetas.value = availabilityResponse.data.filters;
            }

            availability.value = availabilityResponse.data.pagedModel._embedded
                ? availabilityResponse.data.pagedModel._embedded.availabilityResponses.filter((yacht) => yacht.yacht)
                : [];

            resultCount.value = availabilityResponse.data.pagedModel.page.totalElements;
            paginationData.value = availabilityResponse.data.pagedModel.page;


            // Fetch models and shipyards
            filters.value.models = []
            if (filterMetas.value.model?.value.length > 0) {
                filters.value.models = filterMetas.value.model.value.filter((model) => model).sort((a, b) => a.name.localeCompare(b.name));
            }

            filters.value.shipyards = []
            if (filterMetas.value.shipyard?.value.length > 0) {
                filters.value.shipyards = filterMetas.value.shipyard.value.filter((shipyard) => shipyard).sort((a, b) => a.name.localeCompare(b.name));
            }

            state.value = resultCount.value > 0 ? 'idle' : 'noResults';
            isPageLoading.value = false;
        } catch (error) {
            if (!(error?.response?.status === 400)) {
                console.error("Failed to fetch data:", error);
            }
            state.value = 'error';
        } finally {
            isInitialSetup.value = false;
            }
    }

    function loadMore(pageNum) {
        state.value = 'loadingMore';
        console.log(pageNum);
        AvailabilityService.searchAvailabilities(query.value, pageNum)
            .then((data) => {
                let newBoats = ([]);
                newBoats = data.data.pagedModel._embedded.availabilityResponses.filter((yacht) => yacht.yacht);
                availability.value.push(...newBoats);
            })
            .finally(() => {
                    state.value = 'idle';
                }
            )
    }


    const destinationsLoading = ref(false)
    filters.value.destinationsEnabled = true
    const searchDestinations = (query: string) => {
        console.log(query);
        if (query == '' || query == null || query.length < 3) {
            destinationsLoading.value = false
            return
        }

        destinationsLoading.value = true;
        SearchableService.searchByName(query)
            .then((res) => {
                filters.value.destinations = res.data.map((c) => {
                    return DestinationUtils.encode(c)
                }) || [];
            }).finally(() => {
            destinationsLoading.value = false;
        })
    }

    const handleRoute = async (route) => {
        await loadFilters(route)
        await loadSelections(route)
    }
    const loadFilters = async (route) => {

        const res = await Promise.all([
            CountryService.searchByIds(route.query.countryIds || []),
            RegionService.searchByIds(route.query.regionIds || []),
            LocationService.searchByIds(route.query.locationIds || []),
            CityService.searchByIds(route.query.cityIds || []),
            CoreService.getCategories(),
            CoreService.getCharterTypes(),
            CoreService.getSailTypes()
        ]);

        const destinations =
            typeof res[0] == 'object' && 'data' in res[0] && '_embedded' in res[0]['data'] ? res[0].data._embedded.countries.map(DestinationUtils.forEntity("Country").encode) : []
            + typeof res[1] == 'object' && 'data' in res[1] && '_embedded' in res[1]['data'] ? res[1].data._embedded.regions.map(DestinationUtils.forEntity("Region").encode) : []
            + typeof res[2] == 'object' && 'data' in res[2] && '_embedded' in res[2]['data'] ? res[2].data._embedded.locations.map(DestinationUtils.forEntity("Harbour").encode) : []
            + typeof res[3] == 'object' && 'data' in res[3] && '_embedded' in res[3]['data'] ? res[3].data._embedded.cities.map(DestinationUtils.forEntity("Location").encode) : [];


        filters.value.categories = res[4]
        filters.value.charterTypes = res[5].data._embedded.charterTypes
        filters.value.sailTypes = res[6].data._embedded.sailTypes
        filters.value.destinations = destinations

    }

    const loadSelections = async (route) => {
        
        
        defaultSelection.dateFrom = DateUtils.toDate(route.query.dateFrom);
        defaultSelection.dateTo = DateUtils.toDate(route.query.dateTo);

        defaultSelection.categories = Array.isArray(route.query.categories)
            ? route.query.categories.map(Number)
            : route.query.categories
                ? route.query.categories.split(',').map(Number)
                : [];

        defaultSelection.models = route.query.yachtModelIds
            ? route.query.yachtModelIds.split(',').map(Number)
            : [];

        defaultSelection.shipyards = route.query.shipyardIds
            ? route.query.shipyardIds.split(',').map(Number)
            : [];

        defaultSelection.charterTypes = route.query.charterTypeId
            ? route.query.charterTypeId.split(',').map(Number)
            : [];

        categories.value = defaultSelection.categories;

        defaultSelection.passengers = route.query.passengers ? parseInt(route.query.passengers) : defaultSelection.passengers;

        defaultSelection.shouldHaveSkipper = route.query.skipper === "true";

        if (route.query.cabins) {
            defaultSelection.cabins = [parseInt(route.query.cabins), QueryBuilder.MAX];
        }

        if (route.query.minCabins && !route.query.maxCabins) {
            defaultSelection.cabins = [parseInt(route.query.minCabins), QueryBuilder.MAX];
        }

        if (!(route.query.minCabins || route.query.cabins) && route.query.maxCabins) {
            defaultSelection.cabins = [0, parseInt(route.query.maxCabins)];
        }

        if (route.query.minCabins && route.query.maxCabins) {
            defaultSelection.cabins = [parseInt(route.query.minCabins), parseInt(route.query.maxCabins)];
        }

        defaultSelection.wc = [
            route.query.minHeads ? parseInt(route.query.minHeads) : defaultSelection.wc[0],
            route.query.maxHeads ? parseInt(route.query.maxHeads) : defaultSelection.wc[1]
        ];

        defaultSelection.length = [
            route.query.minLength ? parseFloat(route.query.minLength) : defaultSelection.length[0],
            route.query.maxLength ? parseFloat(route.query.maxLength) : defaultSelection.length[1]
        ];

        defaultSelection.destinations = filters.value.destinations ? filters.value.destinations.map((f) => f.value) : [];
 
        defaultSelection.price = [
            route.query.minPrice ? parseInt(route.query.minPrice) : 0,
            route.query.maxPrice ? parseInt(route.query.maxPrice) : QueryBuilder.MAX
        ];
 
        defaultSelection.year = [
            route.query.minBuildYear ? parseInt(route.query.minBuildYear) : 0,
            route.query.maxBuildYear ? parseInt(route.query.maxBuildYear) : QueryBuilder.MAX
        ];
        
        defaultSelection.berths = [
            route.query.minBerths ? parseInt(route.query.minBerths) : defaultSelection.berths[0],
            route.query.maxBerths ? parseInt(route.query.maxBerths) : defaultSelection.berths[1]
        ];
 
        defaultSelection.sailTypeIds = route.query.sailTypeIds 
            ? route.query.sailTypeIds.split(',').map(Number) 
            : defaultSelection.sailTypeIds;
 
        selectedFilters.value = { ...defaultSelection };
    };


    return {
        query,
        recommendationQuery,
        state,
        categories,
        availability,
        filters,
        filterMetas,
        selectedFilters,
        paginationData,
        resultCount,
        isInitialSetup,
        locationChanged,
        destinationsLoading,
        computeSelectedFiltersCountries,
        computeSelectedFiltersRegions,
        computeSelectedFiltersLocations,
        computedOrderedSailItems,
        isLoading,
        isPageLoading,
        isLoadingMore,
        isError,
        isNoResults,
        updatePageUrl,
        setDates,
        toggleSort,
        searchDestinations,
        onClearFilters,
        loadMore,
        search,
        handleRoute,
        loadFilters,
        loadSelections
    }
});